import AddIcon from '@mui/icons-material/Add';
import { Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyButton from '../../../components/AlumlyButton';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { ErrorCodeEnum } from '../../../enums/errorCode';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';
import ModalCreateBoUser from './modalCreateBoUser';
import { RoleT } from './FormEditBoUser';

const BOUserList: React.FC = () => {
  const navigate = useNavigate();

  const [openCreateUserModal, setOpenCreateUserModal] = useState(false);
  const [boUsers, setBoUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    rowCount: 0,
    loading: false,
  });

  const { service: getBoUserService } = useServices(ServiceNameEnum.boUser.index);

  const getBoUsers = async (): Promise<void> => {
    const pageQuery = { page: pagination.page + 1 };
    const { data, error } = await getBoUserService.get({ queryString: pageQuery });
    if (data) {
      setBoUsers(data.items);
      setTotal(data.pagination!.total);
      setPagination({
        page: data.pagination.currentPage - 1,
        pageSize: data.pagination.perPage,
        rowCount: data.pagination.total,
        loading: false,
      });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
      if (errorResponse?.code === ErrorCodeEnum.boUser.userNoPermission) {
        navigate(RouteNameEnum.dashboard.index);
      }
    }
  };

  const { checkPermission } = usePermission();
  const hasPermissionCreateUser = checkPermission([PermissionEnum.code.adminCreate]);
  const hasPermissionEditUser = checkPermission([PermissionEnum.code.adminEditDetail]);

  const columns: any[] = [
    { field: 'email', headerName: 'Email', width: 350 },
    {
      field: 'displayName',
      headerName:
      'Name',
      width: 160,
      flex: 1,
      valueGetter: (params: any) =>
        `${params.row.firstName || ''} ${params.row.lastName || ''}`,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 180,
      flex: 1,
      renderCell: (params: any) => (
        <div>
          {params.row.roles.map((role: RoleT) => (
            <Typography.Text style={{ display: 'block' }}>{role.displayName}</Typography.Text>
          ))}
        </div>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: (params: any) => (
        <div>
          <StatusBadge status={params.row.isActive} />
        </div>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date created',
      width: 160,
      flex: 1,
      cellClassName: 'text-wrap',
      valueGetter: (params: any) =>
        `${DateTimeUtils.dateTimeFormat(params.row.createdAt)}`,
    },
  ];

  useEffect(() => {
    setPagination((prev) => ({ ...prev, loading: true }));
    getBoUsers();
  }, [pagination.page]);

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', justifyContent: 'left' }}>
          <Typography.Text style={{ fontSize: '14px', paddingRight: 8 }}>
            Total
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: '14px' }}>
            {`${total}  Users`}
          </Typography.Text>
        </div>
        {
          (hasPermissionCreateUser && (
            <AlumlyButton
              label="rxv"
              type="submit"
              width={200}
              icon={<AddIcon />}
              onClick={() => setOpenCreateUserModal(true)}
              disabled={!hasPermissionCreateUser}
            >
              Create admin user
            </AlumlyButton>
          )
        )
}

      </div>
      <ModalCreateBoUser
        open={openCreateUserModal}
        onClose={() => setOpenCreateUserModal(false)}
        onSuccess={() => { setOpenCreateUserModal(false); getBoUsers(); }}
      />
      <div style={{ marginTop: 32 }}>
        <AlumlyDataGrid
          rows={boUsers}
          columns={columns}
          {...pagination}
          rowHeight={75}
          autoHeight
          paginationMode="server"
          onPageChange={(page: number) => setPagination((prev) => ({ ...prev, page }))}
          disableColumnMenu
          onRowClick={hasPermissionEditUser
            ? (params: any) => navigate(`${RouteNameEnum.boUser.index}/edit/${params.row.id}`)
            : undefined}
        />
      </div>
    </div>
  );
};
export default BOUserList;
